import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getJobs, updateJob, submitJobForApproval, approveJob, rejectJob, closeJob } from "../../http";
import Loading from "../Loading";
import "react-toastify/dist/ReactToastify.css";

const ViewJobs = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (jobs.length > 0 && !selectedJob) {
      setSelectedJob(jobs[0]);
    }
  }, [jobs, selectedJob]);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      // Make actual API call with cache-busting
      const timestamp = new Date().getTime();
      const response = await getJobs({ _t: timestamp });
      
      console.log('API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Is Array:', Array.isArray(response));
      console.log('Response.data:', response?.data);
      
      // Handle the actual API response format
      if (Array.isArray(response)) {
        console.log('Setting jobs from direct array:', response.length);
        setJobs(response);
      } else if (response && Array.isArray(response.data)) {
        console.log('Setting jobs from response.data:', response.data.length);
        setJobs(response.data);
      } else if (response && typeof response === 'object' && response.status === 200) {
        // Handle object with numeric keys (like {0: {...}, 1: {...}, status: 200})
        const jobArray = Object.keys(response)
          .filter(key => !isNaN(key) && response[key] && response[key]._id)
          .map(key => response[key]);
        console.log('Converting object to array:', jobArray.length);
        setJobs(jobArray);
      } else {
        console.warn('Unexpected API response format:', response);
        setJobs([]);
      }
      
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to fetch jobs");
      setJobs([]); // Clear jobs on error
    } finally {
      setIsLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchQuery || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.role.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = !statusFilter || job.status === statusFilter;
    const matchesLocation = !locationFilter || 
      job.location.toLowerCase().includes(locationFilter.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesLocation;
  });

  console.log('Current jobs state:', jobs);
  console.log('Filtered jobs:', filteredJobs);
  console.log('Search query:', searchQuery);
  console.log('Status filter:', statusFilter);
  console.log('Location filter:', locationFilter);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { class: 'bg-secondary', text: 'Draft' },
      'PUBLISHED': { class: 'bg-success', text: 'Published' },
      'CLOSED': { class: 'bg-danger', text: 'Closed' },
      'APPROVED': { class: 'bg-primary', text: 'Approved' },
      'PENDING_APPROVAL': { class: 'bg-warning', text: 'Pending Approval' }
    };
    
    const config = statusConfig[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const formatSalary = (salaryRange) => {
    if (!salaryRange) return "Salary not specified";
    const { min, max, currency } = salaryRange;
    if (min === max) {
      return `${currency} ${min.toLocaleString()}`;
    }
    return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  const handleSubmitForApproval = async () => {
    try {
      const response = await submitJobForApproval(selectedJob._id);
      toast.success("Job submitted for approval");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(response); // Update selected job with new data
    } catch (error) {
      console.error("Error submitting job for approval:", error);
      toast.error("Failed to submit job for approval");
    }
  };

  const handleApproveJob = async (comments = "") => {
    try {
      const response = await approveJob(selectedJob._id, { comments });
      toast.success("Job approved successfully");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(response); // Update selected job with new data
    } catch (error) {
      console.error("Error approving job:", error);
      toast.error("Failed to approve job");
    }
  };

  const handleRejectJob = async (comments = "") => {
    try {
      const response = await rejectJob(selectedJob._id, { comments });
      toast.success("Job rejected");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(response); // Update selected job with new data
    } catch (error) {
      console.error("Error rejecting job:", error);
      toast.error("Failed to reject job");
    }
  };

  const handleOpenEditModal = () => {
    setEditFormData({
      title: selectedJob.title,
      role: selectedJob.role,
      location: selectedJob.location,
      description: selectedJob.description,
      salaryMin: selectedJob.salaryRange?.min || '',
      salaryMax: selectedJob.salaryRange?.max || '',
      currency: selectedJob.salaryRange?.currency || 'INR'
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({});
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const updateData = {
      title: editFormData.title,
      role: editFormData.role,
      location: editFormData.location,
      description: editFormData.description,
      salaryRange: {
        min: parseInt(editFormData.salaryMin),
        max: parseInt(editFormData.salaryMax),
        currency: editFormData.currency
      }
    };

    try {
      const response = await updateJob(selectedJob._id, updateData);
      toast.success("Job updated successfully");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(response); // Update selected job with new data
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating job:", error);
      toast.error("Failed to update job");
    }
  };

  const handleCloseJob = async () => {
    try {
      const response = await closeJob(selectedJob._id);
      toast.success("Job closed successfully");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(response); // Update selected job with new data
    } catch (error) {
      console.error("Error closing job:", error);
      toast.error("Failed to close job");
    }
  };

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteJob = async () => {
    try {
      // Note: You'll need to add the deleteJob API function to http/index.js
      // For now, I'll simulate the delete
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      toast.success("Job deleted successfully");
      fetchJobs(); // Refresh the jobs list
      setSelectedJob(null); // Clear selected job
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Failed to delete job");
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header">
            <h4>Job Requisitions</h4>
          </div>
          <div className="card-body p-0">
            {/* Filters */}
            <div className="row p-3 border-bottom">
              <div className="col-md-4">
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-search"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search jobs by title or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <select 
                  className="form-control"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="DRAFT">Draft</option>
                  <option value="PUBLISHED">Published</option>
                  <option value="CLOSED">Closed</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING_APPROVAL">Pending Approval</option>
                </select>
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by location..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <button className="btn btn-primary w-100" onClick={fetchJobs}>
                  <i className="fas fa-sync"></i> Refresh
                </button>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="row" style={{ minHeight: '600px' }}>
              {/* Left Column - Job List */}
              <div className="col-md-5 border-right" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {filteredJobs.length === 0 ? (
                  <div className="text-center p-4">
                    <i className="fas fa-briefcase fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No jobs found matching your criteria</p>
                  </div>
                ) : (
                  filteredJobs.map((job) => (
                    <div
                      key={job._id}
                      className={`p-3 border-bottom cursor-pointer job-list-item ${selectedJob?._id === job._id ? 'bg-light' : ''}`}
                      onClick={() => setSelectedJob(job)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex">
                        <div className="mr-3">
                          <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                            <i className="fas fa-building"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1 font-weight-bold">{job.title}</h6>
                          <p className="mb-1 text-muted">{job.role}</p>
                          <p className="mb-1 text-muted small">
                            <i className="fas fa-map-marker-alt"></i> {job.location}
                          </p>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              {getStatusBadge(job.status)}
                              {job.internalPublished && (
                                <span className="badge bg-info ml-1">Internal</span>
                              )}
                              {job.externalPublished && (
                                <span className="badge bg-success ml-1">External</span>
                              )}
                            </div>
                            <small className="text-muted">{formatDate(job.createdAt)}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Right Column - Job Details */}
              <div className="col-md-7" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {selectedJob ? (
                  <div className="p-4">
                    {/* Job Header */}
                    <div className="d-flex mb-4">
                      <div className="mr-3">
                        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                          <i className="fas fa-building fa-2x"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="mb-2">{selectedJob.title}</h4>
                        <p className="text-muted mb-2">{selectedJob.role}</p>
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-map-marker-alt text-muted mr-2"></i>
                          <span className="text-muted">{selectedJob.location}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-clock text-muted mr-2"></i>
                          <span className="text-muted">Posted {formatDate(selectedJob.createdAt)}</span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-users text-muted mr-2"></i>
                          <span className="text-muted">{selectedJob.applicantCount} applicants</span>
                        </div>
                        <div className="d-flex gap-2">
                          {getStatusBadge(selectedJob.status)}
                          {selectedJob.internalPublished && (
                            <span className="badge bg-info">Internal</span>
                          )}
                          {selectedJob.externalPublished && (
                            <span className="badge bg-success">External</span>
                          )}
                          <span className="badge bg-light text-dark">
                            {selectedJob.employmentType ? selectedJob.employmentType.replace('_', ' ').toUpperCase() : 'FULL_TIME'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Salary Information */}
                    <div className="mb-4 p-3 bg-light rounded">
                      <h6 className="font-weight-bold mb-2">Salary Range</h6>
                      <p className="mb-0">{formatSalary(selectedJob.salaryRange)}</p>
                      <small className="text-muted">Per year • {selectedJob.headcount} position(s)</small>
                    </div>

                    {/* Job Description */}
                    <div className="mb-4">
                      <h6 className="font-weight-bold mb-3">About the Job</h6>
                      <p className="text-muted">{selectedJob.description}</p>
                    </div>

                    {/* Responsibilities */}
                    {selectedJob.responsibilities && (
                      <div className="mb-4">
                        <h6 className="font-weight-bold mb-3">Responsibilities</h6>
                        <div className="text-muted" style={{ whiteSpace: 'pre-line' }}>
                          {selectedJob.responsibilities}
                        </div>
                      </div>
                    )}

                    {/* Requirements */}
                    {selectedJob.requirements && (
                      <div className="mb-4">
                        <h6 className="font-weight-bold mb-3">Requirements</h6>
                        <div className="text-muted" style={{ whiteSpace: 'pre-line' }}>
                          {selectedJob.requirements}
                        </div>
                      </div>
                    )}

                    {/* Hiring Manager */}
                    <div className="mb-4 p-3 bg-light rounded">
                      <h6 className="font-weight-bold mb-2">Hiring Manager</h6>
                      <p className="mb-1">{selectedJob.hiringManager?.name}</p>
                      <p className="mb-0 text-muted small">{selectedJob.hiringManager?.email}</p>
                    </div>

                    {/* Approval Status */}
                    <div className="mb-4 p-3 bg-light rounded">
                      <h6 className="font-weight-bold mb-2">Approval Status</h6>
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-warning mr-2">
                          {selectedJob.approval?.status || 'N/A'}
                        </span>
                        {selectedJob.approval?.decidedAt && (
                          <small className="text-muted">
                            Decided {formatDate(selectedJob.approval.decidedAt)}
                          </small>
                        )}
                      </div>
                      {selectedJob.approval?.comments && (
                        <p className="mb-0 text-muted small">
                          "{selectedJob.approval.comments}"
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex gap-2 flex-wrap">
                      <button className="btn btn-primary" onClick={handleOpenEditModal}>
                        <i className="fas fa-edit mr-2"></i>Edit Job
                      </button>
                      {selectedJob.status === 'DRAFT' && (
                        <button className="btn btn-warning" onClick={handleSubmitForApproval}>
                          <i className="fas fa-paper-plane mr-2"></i>Submit for Approval
                        </button>
                      )}
                      {selectedJob.status === 'PENDING_APPROVAL' && (
                        <>
                          <button className="btn btn-success" onClick={() => handleApproveJob()}>
                            <i className="fas fa-check mr-2"></i>Approve
                          </button>
                          <button className="btn btn-danger" onClick={() => handleRejectJob()}>
                            <i className="fas fa-times mr-2"></i>Reject
                          </button>
                        </>
                      )}
                      {(selectedJob.status === 'PUBLISHED' || selectedJob.status === 'PENDING_APPROVAL') && (
                        <button className="btn btn-secondary" onClick={handleCloseJob}>
                          <i className="fas fa-lock mr-2"></i>Close Job
                        </button>
                      )}
                      <button className="btn btn-danger" onClick={handleOpenDeleteModal}>
                        <i className="fas fa-trash mr-2"></i>Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-4">
                    <i className="fas fa-briefcase fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Select a job to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Edit Job Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Job</h5>
              <button type="button" className="close" onClick={handleCloseEditModal}>
                <span>&times;</span>
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Job Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={editFormData.title}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Role</label>
                      <input
                        type="text"
                        className="form-control"
                        name="role"
                        value={editFormData.role}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        className="form-control"
                        name="location"
                        value={editFormData.location}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        className="form-control"
                        name="currency"
                        value={editFormData.currency}
                        onChange={handleEditFormChange}
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Min Salary</label>
                      <input
                        type="number"
                        className="form-control"
                        name="salaryMin"
                        value={editFormData.salaryMin}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Max Salary</label>
                      <input
                        type="number"
                        className="form-control"
                        name="salaryMax"
                        value={editFormData.salaryMax}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Job Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="4"
                    value={editFormData.description}
                    onChange={handleEditFormChange}
                    required
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Job
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Job</h5>
                <button type="button" className="close" onClick={handleCloseDeleteModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                  <h5>Are you sure you want to delete this job?</h5>
                  <p className="text-muted mb-0">
                    <strong>{selectedJob?.title}</strong>
                  </p>
                  <p className="text-muted small">
                    This action cannot be undone. All job data will be permanently deleted.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteJob}>
                  <i className="fas fa-trash mr-2"></i>Delete Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewJobs;